import React, { useEffect, useState } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import StudentPerformanceTable from './StudentPerformanceTable';
import SPPStudentPerformanceTable from './SPPStudentPerformanceTable';
import ClassResultsHeader from 'legacy/src/components/assessment/Results/ClassPerformance/ClassResultsHeader';
import { graphql } from 'react-apollo';
import { assignmentSessionTemplate } from 'legacy-src/queries';
import withSubmissionData from 'legacy-src/utility/withSubmissionData';
import { LoadingSpinner } from 'legacy-packages/ft-cb';
import useQuestionPreview from 'chameleon-src/components/QuestionPreview/hooks/useQuestionPreview';
import {
  getFirstQuestion,
  getReferencesFromQuestions,
} from 'chameleon/src/components/results/resultsQuestions/utils';
import usePerformanceTiers from 'chameleon/src/hooks/usePerformanceTiers';

const StudentPerformanceOverview = (props) => {
  const {
    assignment,
    questions,
    params,
    routes,
    students = [],
    subjectInfo,
    learnosityReportsData,
    learnosityReportsByItemData,
    learnosityReportsByRubricData,
    learnosityReportsResponseAnalysisData,
    learnosityReportsScoringRubricResponseAnalysisData,
    sessionTemplate,
    location,
    router,
    submissionData,
    loading,
    userData,
    pacingFlag,
    studentScoringResults,
    teacherScoringResults,
    isPreApSubject,
    studentResponses,
    scoringRubricResponses,
    isQbGamaAvailable,
    subjectData,
  } = props;

  const { performanceTiers } = usePerformanceTiers(subjectData.id);

  const [
    {
      hoverProps: questionPreviewProps,
      preview: questionPreview,
      previewContainer: questionPreviewContainer,
    },
  ] = useQuestionPreview({
    isAvailable: isQbGamaAvailable,
    items: React.useMemo(() => {
      const firstQuestion = getFirstQuestion(questions);
      return firstQuestion ? [firstQuestion] : [];
    }, [questions]),
    dynamicItems: React.useMemo(
      () => getReferencesFromQuestions(questions),
      [questions],
    ),
    hoveredItemParent: 'th',
    containerPlacement: 'top',
  });

  const scoringDropdownItems = [
    { label: 'Student and Teacher Scores', value: 'all' },
    { label: 'Student Scores', value: 'student' },
    { label: 'Teacher Scores', value: 'teacher' },
  ];

  const [showScoringDropdown, setShowScoringDropdown] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState(0);
  const [selectedScoringDropdownItem, setSelectedScoringDropdownItem] =
    useState(scoringDropdownItems[0]);

  const [selectedView, setSelectedView] = useState('answer');
  const [isPrint, setIsPrint] = useState(false);

  const [resultsOverviewCSVData, setResultsOverviewCSVData] = useState(null);

  const showStudentMovedMessage = students.some((student) =>
    submissionData.some(
      (submission) =>
        submission.user_id.toString() === student.id.toString() &&
        submission.is_copied === true,
    ),
  );

  const isTeacherOrAdminOrCoordinator =
    userData.isTeacher || userData.isAdmin || userData.isCoordinator;
  const { studentScoring, progress, assessment } = assignment;
  const { isSpp } = assessment || {};

  const studentScoringCondition =
    studentScoring ||
    !!progress.studentScored ||
    !!progress.teacherAndStudentScored;

  useEffect(() => {
    if (
      !isPreApSubject &&
      isTeacherOrAdminOrCoordinator &&
      studentScoringCondition
    ) {
      setShowScoringDropdown(true);
    }
  }, []);

  const onScoringDropdownItemChanged = (selectedItem) => {
    setSelectedScoringDropdownItem(selectedItem);
  };

  const downloadCSVResultsOverview = () => {
    // Convert the data array to CSV format
    let csvContent = '';
    resultsOverviewCSVData.forEach((rowArray) => {
      let row = rowArray.join(',');
      csvContent += `${row}\r\n`;
    });

    // Create a Blob object containing the CSV data
    const blob = new Blob([csvContent], { type: 'text/csv' });

    // Create a temporary URL for the Blob
    const url = window.URL.createObjectURL(blob);

    // Create a link element to trigger the download
    const link = document.createElement('a');
    link.setAttribute('href', url);
    const fileName = `${subjectData.name} Student Matrix Report for ${assignment.sectionName} ${assignment.startsAt}.csv`;
    link.setAttribute('download', fileName);

    // Append the link to the document body
    document.body.appendChild(link);

    // Trigger the download
    link.click();

    // Cleanup
    window.URL.revokeObjectURL(url);
    document.body.removeChild(link);
  };

  const tableProps = {
    studentResponses,
    scoringRubricResponses,
    sessionTemplate,
    students,
    items: questions,
    params,
    routes,
    router,
    query: location.query,
    submissionData,
    assignment,
    learnosityReportsData,
    learnosityReportsByItemData,
    learnosityReportsByRubricData,
    learnosityReportsResponseAnalysisData,
    learnosityReportsScoringRubricResponseAnalysisData,
    selectedScoringDropdownItem,
    showScoringDropdown,
    studentScoringResults,
    teacherScoringResults,
    setDropdownPosition,
    performanceTiers,
    questionPreviewProps,
    selectedView,
    setSelectedView,
    isPrint,
    setIsPrint,
    setResultsOverviewCSVData,
  };

  return (
    <div>
      {React.cloneElement(questionPreviewContainer, {
        children: questionPreview,
      })}
      <ClassResultsHeader
        assignment={assignment}
        params={params}
        subjectInfo={subjectInfo}
        students={students}
        // used only for DownLoadStudentReportButton
        learnosityReportsResponseAnalysisData={
          learnosityReportsResponseAnalysisData
        }
        learnosityReportsScoringRubricResponseAnalysisData={
          learnosityReportsScoringRubricResponseAnalysisData
        }
        forStudent
        showStudentMovedMessage={showStudentMovedMessage}
        scoringDropdownItems={scoringDropdownItems}
        selectedScoringDropdownItem={selectedScoringDropdownItem}
        showScoringDropdown={showScoringDropdown}
        onScoringDropdownItemChanged={onScoringDropdownItemChanged}
        dropdownPosition={dropdownPosition}
        loading={loading}
        setSelectedView={setSelectedView}
        selectedView={selectedView}
        downloadCSVResultsOverview={downloadCSVResultsOverview}
        pacingFlag={pacingFlag}
        isPrint={isPrint}
        isSpp={isSpp}
      />
      {!loading ? (
        isSpp ? (
          <SPPStudentPerformanceTable {...tableProps} />
        ) : (
          <StudentPerformanceTable {...tableProps} />
        )
      ) : (
        <LoadingSpinner />
      )}
    </div>
  );
};

const withSessionTemplate = graphql(assignmentSessionTemplate, {
  options: ({ assignment }) => ({
    variables: {
      assignmentId: assignment.id,
    },
    fetchPolicy: 'network-only',
  }),
  props: ({ data }) => {
    if (data.assignment && !data.loading) {
      return {
        sessionTemplate: JSON.parse(data.assignment.sessionTemplate),
        loading: data.loading,
      };
    }

    return { loading: data.loading };
  },
});

const mapStateToProps = ({
  filterControls: { activeEducationPeriod },
  userData,
  featureFlag: { poc_qb_preview_approach_gama: pocQbPreviewApproachGama },
  subject: {
    data: { isPreApSubject },
  },
}) => ({
  activeEducationPeriod,
  userData,
  isPreApSubject,
  isQbGamaAvailable: pocQbPreviewApproachGama,
});

export default compose(
  connect(mapStateToProps),
  withSubmissionData,
  withSessionTemplate,
)(StudentPerformanceOverview);
